import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  StaysWidget,
  StaysWidgetComponent
} from '@com-delta-idp/mnx-sitewide-header-ui';
import {
  AirportDropdownComponent,
  AnalyticsDataPopulateService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {
  AirportDropDown,
  AirportDropDownList
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { PushPipe } from '@ngrx/component';
import { of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { StaysAirportAdapter } from '../../../data-access/adapters/booking-stays/stays-airport-adapter';
import { BookingStaysFacadeService } from '../../../data-access/facades/booking-stays/booking-stays-facade.service';
import { NO_DATA_ERROR } from '../../../data-access/utils/constant';

@Component({
  selector: 'idp-booking-stays',
  standalone: true,
  imports: [
    CommonModule,
    StaysWidgetComponent,
    AirportDropdownComponent,
    PushPipe
  ],
  templateUrl: './booking-stays.component.html',
  styleUrls: ['./booking-stays.component.scss']
})
export class BookingStaysComponent implements OnInit, OnDestroy, OnChanges {
  @Input() viewSearchOptionExpanded = false;
  @Input() isSelected = false;
  @Output() expandEmit = new EventEmitter<boolean>();
  @Output() errorStateChange = new EventEmitter<{
    message: string;
    count: number;
    firstErrorElement: HTMLElement | null;
  }>();

  bookingStaysFacadeService = inject(BookingStaysFacadeService);
  private airportAdapter = inject(StaysAirportAdapter);
  analyticsService = inject(AnalyticsDataPopulateService);
  openModalEvent = false;
  contentloaded = false;
  locationModalContent!: AirportDropDown;
  locationSelected!: AirportDropDownList;
  emptyStaysContent = {} as StaysWidget;
  resetErrors = false;
  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  noDataErrorMessage = NO_DATA_ERROR;

  ngOnInit(): void {
    this.bookingStaysFacadeService.init();
    this.bookingStaysFacadeService.staysWidgetContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((stayWidgetContent) => {
        if (stayWidgetContent && Object.keys(stayWidgetContent).length > 0) {
          this.locationModalContent = stayWidgetContent.airportModalContent;
          this.contentloaded = true;
          this.setupSearchDebounce();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isSelected'] &&
      !changes['isSelected'].firstChange &&
      !this.isSelected
    ) {
      this.resetErrors = true;
      this.openModalEvent = false;
      setTimeout(() => (this.resetErrors = false));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal(value: boolean): void {
    this.openModalEvent = value;
  }

  onLocationSelected(location: AirportDropDownList): void {
    this.locationSelected = location;
  }

  setupSearchDebounce(): void {
    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm) =>
          this.bookingStaysFacadeService.searchLocations(searchTerm).pipe(
            catchError(() => {
              this.bookingStaysFacadeService.airportSearchErrorService.handleApiError(
                searchTerm
              );
              return of([]);
            }),
            map((locations) => ({ locations, searchTerm }))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ locations, searchTerm }) => {
        this.locationModalContent = this.airportAdapter.getAirPortModalContent(
          this.bookingStaysFacadeService.adapter.bookingWidgetAem,
          locations
        );
        this.bookingStaysFacadeService.airportSearchErrorService.handleEmptyResults(
          locations.length,
          searchTerm
        );
      });
  }

  searchValue(searchTerm: string): void {
    this.bookingStaysFacadeService.airportSearchErrorService.clearError();
    this.searchSubject$.next(searchTerm);
  }

  handleAnalytics(element: HTMLElement): void {
    this.analyticsService.populateAnalyticsData(element);
  }
}
